/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars";
import moment from "moment";
import Popup from "reactjs-popup";
import FilterSelector from "../../FilterSelector";
import Statistics from "../../Statistics";
import downArrow from "../../../assets/icons/downArrow.svg";
import { tablePresets } from "./tablePresets";
import { 
  FileNameCell, 
  StatusBadge, 
  RemindersCell, 
  ConnectedEventCell, 
  CalendarRemindersCell,
  MeetingTitleCell,
  MeetingScheduledTimeCell,
  MeetingStatusCell,
  MeetingRealTimeCell,
  MeetingDurationCell,
  MeetingParticipantsCell,
  MeetingRecordingCell,
  MeetingTranscriptCell,
  MeetingRemindersCell,
  UserNameCell
} from "./cellComponents";
import "./styles.scss";

// Range filters hold objects like { min, max }
const isFilterValueActive = (value) => {
  if (value && typeof value === "object") {
    return Object.values(value).some(isFilterValueActive);
  }
  return value !== undefined && value !== null && String(value).trim() !== "";
};

/**
 * Highly customizable DataTable component for AppGrids
 * Supports: custom rendering, filtering, sorting, interactive elements, modals, tooltips
 */
const DataTable = ({
  // Core Data & State
  data = { docs: [], totalPages: 0 },
  loading = false,
  
  // Configuration
  preset,
  columns = [],
  tableWidth = "100%",
  defaultColumnWidth = "9.09%",
  
  // Actions & Data Fetching
  fetchData,
  onLoadMore,
  period,
  limit = 20,
  
  // Features
  features = {
    infiniteScroll: true,
    filtering: false,
    sorting: false,
    rowSelection: false,
    bulkActions: false,
    export: false,
    search: false,
  },
  
  // Custom Components
  customComponents = {},
  
  // Event Handlers
  onRowClick,
  onCellClick,
  onFilterChange,
  onSortChange,
  onSelectionChange,
  selectedRowKeys, // optional controlled selection
  isRowSelectable, // optional (row) => boolean
  
  // Styling
  customStyles = {},
  className = "",
  
  // Advanced Options
  emptyState = {
    text: "No data available",
    image: null,
  },
  
  // Row Actions
  rowActions = [],
  
  // Row Key Function
  rowKey,
  
  // Loading States
  showLoadingIndicator = true,
  loadingThreshold = 0.99,
}) => {
  // Get current location for route-based styling
  const location = useLocation();
  
  // Apply preset configuration if provided
  const presetConfig = preset ? tablePresets[preset] : null;
  const finalColumns = presetConfig?.columns || columns;
  const finalTableWidth = presetConfig?.tableWidth || tableWidth;
  const finalDefaultColumnWidth = presetConfig?.defaultColumnWidth || defaultColumnWidth;
  const finalFeatures = presetConfig?.features || features;
  const finalEmptyState = presetConfig?.emptyState || emptyState;
  const finalRowKey = presetConfig?.rowKey || rowKey;

  // Determine if we're outside app sections (communities, finances, properties)
  // These routes should use the custom height: calc(90vh - 170px)
  const isOutsideAppSection = !location.pathname.startsWith('/app/');
  
  // Build final className with conditional custom height class
  const finalClassName = `${className} ${isOutsideAppSection ? 'data-table_custom_height' : ''}`.trim();

  // State Management
  const [curPage, setCurPage] = useState(2);
  const [innerLoading, setInnerLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({});
  const [internalSelectedRows, setInternalSelectedRows] = useState([]);
  const selectedRows = selectedRowKeys ?? internalSelectedRows;
  const setSelectedRows = selectedRowKeys ? () => {} : setInternalSelectedRows;
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  
  // Refs
  const tableRef = useRef();
  const scrollRef = useRef();

  // Reset loading state when new data arrives
  useEffect(() => {
    if (data?.docs?.length > 0) {
      setInnerLoading(false);
    }
  }, [data?.docs]);

  // Reset page counter when data changes (new search/filter)
  useEffect(() => {
    setCurPage(2);
  }, [data?.totalPages]);

  // Infinite Scroll Handler
  const handleUpdate = useCallback((values) => {
    if (!finalFeatures.infiniteScroll) return;
    
    const { scrollTop, scrollHeight, clientHeight } = values;
    const pad = 0;
    const t = (Math.round(scrollTop) + pad) / (scrollHeight - clientHeight);
    
    if (t >= loadingThreshold && data?.totalPages >= curPage && !innerLoading) {
      setInnerLoading(true);
      
      if (onLoadMore) {
        onLoadMore(curPage);
        setCurPage(curPage + 1);
      } else if (fetchData) {
        const payload = {
          page: curPage,
          limit,
          startDate: period?.startDate || "",
          endDate: period?.endDate || "",
          filters,
          sort: sortConfig,
          search: searchTerm,
          onSuccess: () => setInnerLoading(false),
          onError: () => setInnerLoading(false),
        };
        
        fetchData(payload);
        setCurPage(curPage + 1);
      }
    }
  }, [curPage, innerLoading, data?.totalPages, finalFeatures.infiniteScroll, loadingThreshold, limit, period, filters, sortConfig, searchTerm, fetchData, onLoadMore]);

  // Initial Data Load
  useEffect(() => {
    if (fetchData) {
      const payload = {
        page: 1,
        limit,
        startDate: period?.startDate || "",
        endDate: period?.endDate || "",
        filters,
        sort: sortConfig,
        search: searchTerm,
      };
      
      fetchData(payload);
      setCurPage(2);
    }
  }, []);

  // Period Change Handler
  useEffect(() => {
    if (period?.startDate !== "" && period?.startDate !== null && fetchData) {
      const payload = {
        page: 1,
        limit,
        startDate: period?.startDate || "",
        endDate: period?.endDate || "",
        filters,
        sort: sortConfig,
        search: searchTerm,
      };
      
      fetchData(payload);
      setCurPage(2);
    }
  }, [period]);

  // Filter Change Handler
  const handleFilterChange = useCallback((columnKey, filterValue) => {
    const newFilters = { ...filters, [columnKey]: filterValue };
    setFilters(newFilters);
    
    const payload = {
      page: 1,
      limit,
      startDate: period?.startDate || "",
      endDate: period?.endDate || "",
      filters: newFilters,
      sort: sortConfig,
      search: searchTerm,
    };
    
    fetchData(payload);
    setCurPage(2);
    
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  }, [filters, period, sortConfig, searchTerm, limit, fetchData, onFilterChange]);

  // Sort Change Handler
  const handleSortChange = useCallback((columnKey) => {
    const newSortConfig = {
      field: columnKey,
      direction: sortConfig.field === columnKey && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    };
    
    setSortConfig(newSortConfig);
    
    const payload = {
      page: 1,
      limit,
      startDate: period?.startDate || "",
      endDate: period?.endDate || "",
      filters,
      sort: newSortConfig,
      search: searchTerm,
    };
    
    fetchData(payload);
    setCurPage(2);
    
    if (onSortChange) {
      onSortChange(newSortConfig);
    }
  }, [sortConfig, period, filters, searchTerm, limit, fetchData, onSortChange]);

  // Search Handler
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    
    const payload = {
      page: 1,
      limit,
      startDate: period?.startDate || "",
      endDate: period?.endDate || "",
      filters,
      sort: sortConfig,
      search: value,
    };
    
    fetchData(payload);
    setCurPage(2);
  }, [period, filters, sortConfig, limit, fetchData]);

  // Row Selection Handler
  const handleRowSelection = useCallback((rowId, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedRows, rowId];
    } else {
      newSelection = selectedRows.filter(id => id !== rowId);
    }
    
    setSelectedRows(newSelection);
    
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  }, [selectedRows, onSelectionChange]);

  // Cell Renderer
  const renderCell = useCallback((column, row, rowIndex) => {
    const { key, render, width = finalDefaultColumnWidth, className = "" } = column;
    const value = row[key];
    
    // Custom render function
    if (typeof render === 'function') {
      return render(value, row, rowIndex);
    }
    
    // Custom component
    if (render?.component) {
      let Component = render.component;
      
      // Handle string component names
      if (typeof Component === 'string') {
        const componentMap = {
          'FileNameCell': FileNameCell,
          'StatusBadge': StatusBadge,
          'RemindersCell': RemindersCell,
          'ConnectedEventCell': ConnectedEventCell,
          'CalendarRemindersCell': CalendarRemindersCell,
          'MeetingTitleCell': MeetingTitleCell,
          'MeetingScheduledTimeCell': MeetingScheduledTimeCell,
          'MeetingStatusCell': MeetingStatusCell,
          'MeetingRealTimeCell': MeetingRealTimeCell,
          'MeetingDurationCell': MeetingDurationCell,
          'MeetingParticipantsCell': MeetingParticipantsCell,
          'MeetingRecordingCell': MeetingRecordingCell,
          'MeetingTranscriptCell': MeetingTranscriptCell,
          'MeetingRemindersCell': MeetingRemindersCell,
          'UserNameCell': UserNameCell
        };
        Component = componentMap[Component];
      }
      
      if (Component) {
        const props = { value, row, rowIndex, ...render.props };
        return <Component {...props} />;
      }
    }
    
    // Default rendering based on type
    if (render?.type) {
      switch (render.type) {
        case 'date':
          return value ? moment(value).format(render.format || 'DD.MM.YYYY') : '---';
        case 'time':
          return value ? moment(value).format(render.format || 'HH:mm') : '---';
        case 'datetime':
          return value ? moment(value).format(render.format || 'DD.MM.YYYY HH:mm') : '---';
        case 'number':
          return value !== null && value !== undefined ? value : '---';
        case 'boolean':
          return value ? 'Yes' : 'No';
        case 'currency':
          return value ? `${value.toFixed(2)} BGN` : '---';
        case 'fileSize':
          return formatFileSize(value);
        case 'count':
          return value || '0';
        default:
          return value || '---';
      }
    }
    
    // Default text rendering
    return value || '---';
  }, [finalDefaultColumnWidth]);

  // Header Renderer
  const renderHeader = useCallback((column, columnIndex) => {
    const { 
      key, 
      header, 
      width = finalDefaultColumnWidth, 
      sortable = false, 
      filterable = false,
      filter = {},
      className = "",
      headerComponent
    } = column;
    
    // Custom header component
    if (headerComponent) {
      const Component = headerComponent;
      return <Component column={column} columnIndex={columnIndex} />;
    }

    const hasActiveFilter = isFilterValueActive(filters[key]);
    const isFilterOpen = activeFilterColumn === key;
    
    return (
      <div 
        className={`header-cont ${className}`} 
        key={columnIndex}
        style={{ width: width }}
      >
        <div 
          className={`header ${sortable ? 'sortable' : ''} ${filterable ? 'filterable' : ''} ${sortConfig.field === key ? `sort-${sortConfig.direction}` : ''}`}
        >
          <span 
            className="header-text"
            onClick={() => sortable && handleSortChange(key)}
          >
            {header}
          </span>
          
          {/* Sort Icon */}
          {sortable && (
            <span 
              className="sort-icon"
              onClick={() => handleSortChange(key)}
            >
              {sortConfig.field === key ? 
                (sortConfig.direction === 'asc' ? '↑' : '↓') : 
                '↕'
              }
            </span>
          )}
          
          {/* Filter Icon with Popup */}
          {filterable && (
            <Popup
              className="popup-filter-options-container"
              trigger={
                <span 
                  className={`filter-arrow ${hasActiveFilter ? 'has-filter' : ''} ${isFilterOpen ? 'open' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveFilterColumn(isFilterOpen ? null : key);
                  }}
                >
                  <img 
                    src={downArrow} 
                    alt="Filter arrow" 
                    style={{ 
                      width: '12px', 
                      height: 'auto',
                      transition: 'transform 0.2s ease'
                    }} 
                  />
                </span>
              }
              position="bottom left"
              closeOnDocumentClick
              open={isFilterOpen}
              onClose={() => setActiveFilterColumn(null)}
            >
              {(close) => (
                <FilterSelector
                  filterType={filter.type || "text"}
                  placeholder={filter.placeholder || "Enter value..."}
                  currentValue={filters[key] || ""}
                  options={filter.options || []}
                  onApply={(result) => {
                    handleFilterChange(key, result.value);
                    close();
                    setActiveFilterColumn(null);
                  }}
                />
              )}
            </Popup>
          )}
        </div>
      </div>
    );
  }, [finalDefaultColumnWidth, sortConfig, filters, handleSortChange, handleFilterChange, activeFilterColumn]);

  // Row Renderer
  const renderRow = useCallback((row, rowIndex) => {
    // Generate unique key for this row
    const uniqueRowKey = finalRowKey ? finalRowKey(row, rowIndex) : (row._id || rowIndex);
    const isSelected = selectedRows.includes(uniqueRowKey);
    const isHovered = hoveredRow === rowIndex;
    
    return (
      <div 
        className={`table-body-row ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
        key={uniqueRowKey}
        style={{ width: finalTableWidth }}
        onClick={() => onRowClick && onRowClick(row, rowIndex)}
        onMouseEnter={() => setHoveredRow(rowIndex)}
        onMouseLeave={() => setHoveredRow(null)}
      >
        {/* Row Selection Checkbox */}
        {finalFeatures.rowSelection && (
          <div className="row-selection" style={{ width: '40px' }}>
            {(!isRowSelectable || isRowSelectable(row)) && (
              <input 
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handleRowSelection(uniqueRowKey, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        )}
        
        {/* Data Cells */}
        {finalColumns.map((column, columnIndex) => (
          <div 
            key={column.key}
            className={`row-data ${column.className || ''}`}
            style={{ width: column.width || finalDefaultColumnWidth }}
            onClick={(e) => {
              e.stopPropagation();
              if (onCellClick) {
                onCellClick(column.key, row, rowIndex);
              }
            }}
          >
            {renderCell(column, row, rowIndex)}
          </div>
        ))}
        
        {/* Row Actions */}
        {rowActions.length > 0 && (
          <div className="row-actions" style={{ width: '80px' }}>
            <RowActions 
              actions={rowActions}
              row={row}
              rowIndex={rowIndex}
            />
          </div>
        )}
      </div>
    );
  }, [finalColumns, finalTableWidth, selectedRows, hoveredRow, finalFeatures.rowSelection, finalDefaultColumnWidth, onRowClick, onCellClick, rowActions, handleRowSelection, finalRowKey, isRowSelectable]);

  // Utility Functions
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = (bytes / Math.pow(1024, i)).toFixed(2);
    
    return `${size} ${sizes[i]}`;
  };

  const selectableRowKeys = (data?.docs || [])
    .map((row, index) => ({ row, key: finalRowKey ? finalRowKey(row, index) : (row._id || index) }))
    .filter(({ row }) => !isRowSelectable || isRowSelectable(row))
    .map(({ key }) => key);

  // Keep headers visible when filters are active so they can be cleared
  const hasActiveFilters = Object.values(filters).some(isFilterValueActive);

  // Empty State
  if (!data?.docs?.length && !loading && !hasActiveFilters) {
    return (
      <div className={`data-table-empty ${finalClassName}`} style={customStyles.empty}>
        <div className="empty-state-container">
          <div className="empty-state-text">{finalEmptyState.text}</div>
          {finalEmptyState.image && (
            <div 
              className="empty-state-image"
              style={{ backgroundImage: `url(${finalEmptyState.image})` }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`data-table ${finalClassName}`} style={customStyles.container}>
      {/* Search Bar */}
      {finalFeatures.search && (
        <div className="search-container" style={customStyles.search}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
        </div>
      )}
      
      {/* Bulk Actions */}
      {finalFeatures.bulkActions && selectedRows.length > 0 && (
        <div className="bulk-actions" style={customStyles.bulkActions}>
          <span>{selectedRows.length} items selected</span>
          {/* Add bulk action buttons here */}
        </div>
      )}
      
      {/* Table */}
      <Scrollbars
        style={{ height: "100%", width: "100%"}}
        onUpdate={handleUpdate}
        ref={scrollRef}
        id="data-table-scrollbar"
      >
        <div className="table-header" style={{ width: finalTableWidth, ...customStyles.header }}>
          {finalFeatures.rowSelection && (
            <div className="header-cont" style={{ width: '40px' }}>
              <div className="header">
                <input 
                  type="checkbox"
                  checked={selectableRowKeys.length > 0 && selectableRowKeys.every((id) => selectedRows.includes(id))}
                  disabled={!selectableRowKeys.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allIds = selectableRowKeys;
                      setSelectedRows(allIds);
                      if (onSelectionChange) onSelectionChange(allIds);
                    } else {
                      setSelectedRows([]);
                      if (onSelectionChange) onSelectionChange([]);
                    }
                  }}
                />
              </div>
            </div>
          )}
          
          {finalColumns.map((column, index) => renderHeader(column, index))}
          
          {rowActions.length > 0 && (
            <div className="header-cont" style={{ width: '80px' }}>
              <div className="header">Actions</div>
            </div>
          )}
        </div>
        
        {data?.docs?.map((row, index) => renderRow(row, index))}

        {!data?.docs?.length && !loading && (
          <div
            className="empty-state-text"
            style={{ width: finalTableWidth, textAlign: 'center', padding: '40px 0', fontSize: '16px', color: '#666', textTransform: 'uppercase' }}
          >
            {finalEmptyState.text}
          </div>
        )}
        
        {/* Custom Footer */}
        {preset && tablePresets[preset]?.customFooter && (
          <div className="table-custom-footer">
            {tablePresets[preset].customFooter(data?.docs || [])}
          </div>
        )}
        
        {/* Loading Indicator */}
        {innerLoading && showLoadingIndicator && (
          <div className="inner-loader-container" style={customStyles.loading}>
            <Statistics.LoaderInline center />
          </div>
        )}
      </Scrollbars>
    </div>
  );
};

// Filter Component
const FilterComponent = ({ column, value, onChange }) => {
  const { filter } = column;
  
  if (!filter) return null;
  
  switch (filter.type) {
    case 'select':
      return (
        <select 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)}
          className="filter-select"
        >
          <option value="">All</option>
          {filter.options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={filter.placeholder || 'Filter...'}
          className="filter-input"
        />
      );
    
    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="filter-date"
        />
      );
    
    default:
      return null;
  }
};

// Row Actions Component
const RowActions = ({ actions, row, rowIndex }) => {
  const [showActions, setShowActions] = useState(false);
  
  return (
    <div className="row-actions-container">
      <button 
        className="actions-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setShowActions(!showActions);
        }}
      >
        ⋮
      </button>
      
      {showActions && (
        <div className="actions-dropdown">
          {actions.map((action, index) => (
            <button
              key={index}
              className="action-item"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(row, rowIndex);
                setShowActions(false);
              }}
            >
              {action.icon && <span className="action-icon">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Default export with Redux connection
const mapStateToProps = () => ({});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DataTable); 